import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import API_CONFIG from '../config/api';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
    const [lastMessage, setLastMessage] = useState(null);
    const [readyState, setReadyState] = useState(0); // 0: CONNECTING, 1: OPEN, etc.
    const ws = useRef(null);

    useEffect(() => {
        // Derive WS URL from API BASE_URL if possible, otherwise use current host
        let defaultWsUrl;
        try {
            const baseUrl = API_CONFIG.BASE_URL;
            if (baseUrl.startsWith('http')) {
                const url = new URL(baseUrl);
                const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
                defaultWsUrl = `${wsProtocol}//${url.host}/ws/notifications/`;
            } else {
                // Fallback to current host
                const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                defaultWsUrl = `${wsProtocol}//${window.location.host}/ws/notifications/`;
            }
        } catch (e) {
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            defaultWsUrl = `${wsProtocol}//${window.location.host}/ws/notifications/`;
        }

        const baseSocketUrl = process.env.REACT_APP_WS_URL || defaultWsUrl;
        let retryDelay = 2000;
        let closedByUnmount = false;
        let retryTimer = null;

        const connect = () => {
            const token = localStorage.getItem('accessToken') || '';
            const socketUrl = `${baseSocketUrl}?token=${encodeURIComponent(token)}`;
            ws.current = new WebSocket(socketUrl);

            ws.current.onopen = () => {
                console.log('WebSocket Connected');
                retryDelay = 2000;
                setReadyState(1);
            };

            ws.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    setLastMessage(data);
                } catch (e) {
                    console.error("Error parsing WebSocket message", e);
                }
            };

            ws.current.onclose = () => {
                setReadyState(3);
                if (!closedByUnmount) {
                    // auto-reconnect with capped backoff so realtime survives
                    // deploys, sleep/wake and network blips
                    retryTimer = setTimeout(connect, retryDelay);
                    retryDelay = Math.min(retryDelay * 2, 30000);
                }
            };

            ws.current.onerror = () => { /* onclose handles retry */ };
        };

        connect();

        return () => {
            closedByUnmount = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (ws.current) ws.current.close();
        };
    }, []);

    const sendMessage = (message) => {
        if (ws.current && ws.current.readyState === 1) {
            ws.current.send(JSON.stringify(message));
        }
    };

    return (
        <WebSocketContext.Provider value={{ sendMessage, lastMessage, readyState }}>
            {children}
        </WebSocketContext.Provider>
    );
};

export const useWebSocket = () => {
    return useContext(WebSocketContext);
};
